/**
 * Utility functions for Wealthica display formatting and data organization
 */
import { isDemoProvider } from './identifiers';
import { isDebugEnabled } from '@/utils/debug';

// Use single global debug switch
const DEBUG: boolean = isDebugEnabled();

/**
 * Format currency for display - DEPRECATED: Use formatCurrency from @/lib/portfolioCalculations instead
 * @param value - The numeric value to format
 * @param currency - The currency code (defaults to CAD)
 * @returns Formatted currency string
 */
export function formatCurrency(value: number, currency: string = 'CAD'): string {
  // Handle null, undefined, NaN, or invalid values
  const numValue = Number(value);
  const safeValue = Number.isFinite(numValue) ? numValue : 0;

  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: currency || 'CAD',
    minimumFractionDigits: 2
  }).format(safeValue);
}

/**
 * Get a user-friendly description for account types
 * @param type - The account type code from Wealthica
 * @returns Human-readable account type description
 */
export function getAccountTypeDescription(type?: string): string {
  switch (type?.toLowerCase()) {
    case 'checking':
      return 'Checking Account';
    case 'savings':
      return 'Savings Account';
    case 'rrsp':
      return 'RRSP';
    case 'tfsa':
      return 'TFSA';
    case 'rrif':
      return 'RRIF';
    case 'lira':
      return 'LIRA';
    case 'lif':
      return 'LIF';
    case 'resp':
      return 'RESP';
    case 'joint':
      return 'Joint Account';
    case 'margin':
      return 'Margin Account';
    case 'creditcard':
      return 'Credit Card';
    default:
      return type ? `${type.charAt(0).toUpperCase()}${type.slice(1)}` : 'Unknown Account Type';
  }
}

/**
 * Group accounts by institution for better organization in the UI
 * @param accounts - Array of account data from Wealthica
 * @returns Array of institution groups with associated accounts
 */
export interface InstitutionGroup {
  id: string;
  name: string;
  logoUrl: string | null;
  accounts: any[];
  totalBalance: number;
  currency: string;
}


/**
 * Helper function to determine the correct institution name
 * @param account - Account data from Wealthica  
 * @param institutionId - The institution ID
 * @returns The correct institution name
 */
function getInstitutionName(account: any, institutionId: string): string {
  // Handle demo provider with shared detection helper
  if (isDemoProvider(account.providerName || account.provider_name, account.institutionIdentifier || account.institution_identifier, institutionId)) {
    return 'Wealthica Demo';
  }
  // Use the provided institution name with fallbacks
  return account.institutionName || account.institution_name || 'Unknown Institution';
}

export function groupAccountsByInstitution(accounts: any[]): InstitutionGroup[] {
  if (DEBUG) {
    console.log('🏛️ DISPLAY UTILS: groupAccountsByInstitution input', {
      accountsLength: accounts?.length || 0,
      accountsType: typeof accounts,
      firstAccountSample: accounts?.[0] ? {
        id: accounts[0].id,
        name: accounts[0].name,
        institutionId: accounts[0].institutionId || accounts[0].institution_id,
        providerName: accounts[0].providerName || accounts[0].provider_name
      } : null
    });
  }
  
  if (!accounts || (accounts?.length || 0) === 0) {
    if (DEBUG) console.log('🏛️ DISPLAY UTILS: No accounts provided, returning empty array');
    return [];
  }

  // Group by institution
  const groupedByInstitution: Record<string, any[]> = {};
  accounts.forEach(account => {
    // Use camelCase (institutionId) with fallback to snake_case (institution_id) for compatibility
    const institutionId = account.institutionId || account.institution_id || 'unknown';
    if (!groupedByInstitution[institutionId]) {
      groupedByInstitution[institutionId] = [];
    }
    groupedByInstitution[institutionId].push(account);
  });
  
  if (DEBUG) {
    console.log('🏛️ DISPLAY UTILS: Grouped accounts by institution', {
      institutionCount: Object.keys(groupedByInstitution).length,
      institutionIds: Object.keys(groupedByInstitution),
      accountsPerInstitution: Object.entries(groupedByInstitution).map(([id, accs]) => ({
        institutionId: id,
        accountCount: accs.length
      }))
    });
  }

  // Create institution groups with demo filtering
  return Object.entries(groupedByInstitution).map(([institutionId, institutionAccounts]) => {
    // Use the first account's institution info (all should be the same)
    const firstAccount = institutionAccounts[0];
    
    // Get correct institution name using our helper function
    const institutionName = getInstitutionName(firstAccount, institutionId);
    // Tolerant logo extraction across possible response shapes
    const institutionLogoUrl =
      firstAccount.institutionLogoUrl ||
      firstAccount.institution_logo_url ||
      (firstAccount.institution && (firstAccount.institution.logoUrl || firstAccount.institution.logo_url || firstAccount.institution.logo)) ||
      (firstAccount.provider && (firstAccount.provider.logoUrl || firstAccount.provider.logo)) ||
      null;
    
    // REMOVED BROKEN FILTERING LOGIC - was filtering out all real accounts
    const filteredAccounts = institutionAccounts; // Keep ALL accounts
    
    if (DEBUG) {
      console.log('🏛️ DISPLAY UTILS: No filtering applied', {
        institutionId,
        institutionName,
        accountsCount: filteredAccounts.length
      });
    }
    
    // Sum balances - make sure to parse as floats and filter out any NaN values
    const validBalances = filteredAccounts
      .map(a => parseFloat(a.balance) || 0)
      .filter(b => !isNaN(b));
      
    const totalBalance = validBalances.reduce((sum, balance) => sum + balance, 0);
    
    // We now use the variables we defined earlier that handle both naming conventions
    return {
      id: institutionId,
      name: institutionName || 'Unknown Institution',
      logoUrl: institutionLogoUrl || null,
      accounts: filteredAccounts,
      totalBalance,
      currency: firstAccount.currency || 'CAD' // Assume all accounts in a group have same currency
    };
  }).filter(institution => institution.accounts.length > 0); // Remove institutions with no accounts after filtering
}
